/**
 * Slide-in panel showing the latest manual job run result.
 */

import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch } from '@/app/store';
import { clearLastRun, selectLastRunResponse } from '@/app/store/jobsSlice';

import './JobRunResultDrawer.scss';

function formatResultValue(value: unknown): string {
  if (value === null) return 'null';
  if (typeof value === 'object') {
    return JSON.stringify(value, null, 2);
  }
  return String(value);
}

export const JobRunResultDrawer: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const lastRunResult = useSelector(selectLastRunResponse);

  if (lastRunResult == null) {
    return null;
  }

  const resultEntries = Object.entries(lastRunResult.result);

  return (
    <div className="job-run-result-drawer" role="dialog" aria-label="Job run result">
      <button
        type="button"
        className="job-run-result-drawer__backdrop"
        aria-label="Close result panel"
        onClick={() => dispatch(clearLastRun())}
      />
      <div className="job-run-result-drawer__panel">
        <div className="job-run-result-drawer__header">
          <h2 className="job-run-result-drawer__title">Run result</h2>
          <button
            type="button"
            className="job-run-result-drawer__close"
            aria-label="Close"
            onClick={() => dispatch(clearLastRun())}
          >
            ×
          </button>
        </div>
        <div className="job-run-result-drawer__body">
          <dl className="job-run-result-drawer__meta">
            <div>
              <dt>Job</dt>
              <dd>{lastRunResult.job_name}</dd>
            </div>
            <div>
              <dt>Dry run</dt>
              <dd>{lastRunResult.dry_run ? 'Yes' : 'No'}</dd>
            </div>
            <div>
              <dt>Status</dt>
              <dd>{lastRunResult.status}</dd>
            </div>
            <div>
              <dt>Duration (s)</dt>
              <dd>{lastRunResult.duration_seconds}</dd>
            </div>
            {lastRunResult.error != null && lastRunResult.error.length > 0 && (
              <div>
                <dt>Error</dt>
                <dd className="job-run-result-drawer__error">{lastRunResult.error}</dd>
              </div>
            )}
          </dl>

          {resultEntries.length > 0 && (
            <section className="job-run-result-drawer__section">
              <h3 className="job-run-result-drawer__section-title">Result</h3>
              <ul className="job-run-result-drawer__kv">
                {resultEntries.map(([key, value]) => (
                  <li key={key}>
                    <span className="job-run-result-drawer__key">{key}</span>
                    <span className="job-run-result-drawer__val">{formatResultValue(value)}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <section className="job-run-result-drawer__section">
            <h3 className="job-run-result-drawer__section-title">Logs</h3>
            <pre
              className="job-run-result-drawer__logs"
              data-testid="job-run-logs"
            >
              {lastRunResult.logs.join('\n')}
            </pre>
          </section>
        </div>
      </div>
    </div>
  );
};

export default JobRunResultDrawer;
